import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, ExternalLink, Clock, Sparkles } from "lucide-react";
import { getPersonaHistory, getCreatorId } from "../lib/api";

function fmt(ts) {
  if (!ts) return "";
  try {
    const d = new Date(ts);
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch { return ""; }
}

export default function PersonaHistory() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { setData(await getPersonaHistory(getCreatorId())); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="flex items-center justify-center py-32"><Loader2 className="h-6 w-6 animate-spin text-[#FF3B30]" /></div>;

  if (!data || data.count === 0) {
    return (
      <div className="mx-auto max-w-[1200px] px-6 py-20 md:px-10">
        <p className="font-mono text-[11px] uppercase tracking-[0.4em] text-[#FF3B30]">// Vol. 07</p>
        <h1 className="mt-2 font-display text-6xl uppercase text-white md:text-8xl">Persona<br/>History</h1>
        <p className="mt-4 max-w-lg text-neutral-400">
          No snapshots yet. Hit &quot;Share Link&quot; on your Persona page and every share also gets saved here so you can watch your taste evolve.
        </p>
        <Link to="/personality" className="mt-6 inline-block border border-white/20 bg-[#FF3B30] px-4 py-2 font-mono text-[11px] uppercase tracking-widest text-white hover:bg-white hover:text-black">
          Snapshot my persona
        </Link>
      </div>
    );
  }

  // Detect persona shifts between adjacent snapshots (data is sorted newest-first)
  const withShift = data.snapshots.map((s, i) => {
    const next = data.snapshots[i + 1];
    const shifted = next && next.personality_type !== s.personality_type;
    return { ...s, shifted, prev_type: next?.personality_type };
  });

  return (
    <div className="mx-auto max-w-[1200px] px-6 py-10 md:px-10">
      <p className="font-mono text-[11px] uppercase tracking-[0.4em] text-[#FF3B30]">// Vol. 07</p>
      <h1 className="mt-2 font-display text-6xl uppercase text-white md:text-8xl">Persona<br/>History</h1>
      <p className="mt-4 max-w-lg text-sm text-neutral-400">
        {data.count} snapshot{data.count === 1 ? "" : "s"} saved. Latest first — watch your persona drift as your catalogue grows.
      </p>

      <ol className="mt-12 relative">
        {/* Vertical rail */}
        <div className="pointer-events-none absolute left-4 top-0 bottom-0 w-px bg-white/10 md:left-6" />
        {withShift.map((s, i) => (
          <li
            key={s.share_id}
            data-testid={`history-snapshot-${s.share_id}`}
            className="relative pl-12 pb-10 md:pl-16"
            style={{ animation: `fadeUp 0.5s ${i * 0.05}s both ease-out` }}
          >
            <div className={`absolute left-[9px] top-2 h-4 w-4 border ${i === 0 ? "bg-[#FF3B30] border-[#FF3B30]" : "bg-black border-white/30"} md:left-[17px]`} />
            <div className="border border-white/15 bg-neutral-950 p-5 md:p-6">
              <div className="flex flex-wrap items-baseline justify-between gap-3">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                    <Clock className="mr-1 inline h-3 w-3" />
                    {fmt(s.created_at)}
                    {i === 0 && <span className="ml-3 border border-[#FF3B30] bg-[#FF3B30] px-2 py-0.5 text-white">Current</span>}
                  </p>
                  <h3 className="mt-2 font-display text-3xl uppercase leading-tight text-white md:text-5xl">
                    {s.personality_type}
                  </h3>
                  {s.shifted && s.prev_type && (
                    <p className="mt-2 flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-[#FF3B30]">
                      <Sparkles className="h-3 w-3" /> Shift from &quot;{s.prev_type}&quot;
                    </p>
                  )}
                </div>
                <Link
                  to={s.url_path}
                  data-testid={`history-view-${s.share_id}`}
                  className="flex items-center gap-2 border border-white/20 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-white hover:bg-white hover:text-black"
                >
                  View snapshot <ExternalLink className="h-3 w-3" />
                </Link>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-4 border-t border-white/10 pt-4 md:grid-cols-4">
                <Cell label="Top Genre" value={s.top_genre} accent />
                <Cell label="Anime" value={s.total_anime_in_catalogue} />
                <Cell label="Avg Rating" value={s.average_rating_given ?? "—"} />
                <Cell label="Completion" value={s.completion_rate_percent != null ? `${s.completion_rate_percent}%` : "—"} />
              </div>

              {s.secondary_genres?.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {s.secondary_genres.map((g) => (
                    <span key={g} className="border border-white/15 px-2 py-1 font-mono text-[9px] uppercase tracking-widest text-neutral-300">
                      {g}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

function Cell({ label, value, accent }) {
  return (
    <div className={accent ? "border-l-2 border-[#FF3B30] pl-3" : "pl-3"}>
      <p className="font-mono text-[9px] uppercase tracking-widest text-neutral-500">{label}</p>
      <p className="mt-1 font-display text-2xl uppercase text-white">{value}</p>
    </div>
  );
}
